import { functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Service to handle Google Drive Media Uploads.
 * Uses a Direct-to-Drive architecture for $0 bandwidth cost.
 */
class MediaService {
    /**
     * Request a secure, signed upload URL from the Firebase backend.
     */
    async getUploadUrl(file, game) {
        try {
            const getUrlFunction = httpsCallable(functions, 'getUploadUrl');

            const gameDateObj = new Date(game.date);
            const year = gameDateObj.getFullYear();
            const opponent = game.opposition || game.awayTeam || 'Unknown Opponent';
            const tournament = game.tournamentName || 'Friendlies';
            const gameName = `${game.date} vs ${opponent}`;

            const requestData = {
                fileName: file.name,
                mimeType: file.type,
                year: year,
                tournamentName: tournament,
                gameName: gameName
            };

            console.log('Requesting upload URL for:', requestData);
            const result = await getUrlFunction(requestData);

            return result.data; // { uploadUrl: string, folderId: string }
        } catch (error) {
            console.error('Error fetching upload URL:', error);
            throw error;
        }
    }

    /**
     * Directly upload a file to the provided Google Drive URL.
     * Uses XMLHttpRequest to accurately track byte-for-byte upload progress.
     */
    /**
     * Uploads the file to the Google Drive Resumable Session URI in chunks.
     * Supports massive files (e.g. 60GB videos) and resumes intelligently on network drop.
     */
    async uploadFileDirectly(file, uploadUrl, onProgress) {
        const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunk size
        let start = 0;
        let end = Math.min(CHUNK_SIZE, file.size);
        let retries = 0;
        const MAX_RETRIES = 10;

        while (start < file.size) {
            try {
                const chunk = file.slice(start, end);

                const response = await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();

                    xhr.upload.onprogress = (event) => {
                        if (event.lengthComputable && onProgress) {
                            const loadedSoFar = start + event.loaded;
                            const percentComplete = Math.round((loadedSoFar / file.size) * 100);
                            if (percentComplete < 100) onProgress(percentComplete);
                        }
                    };

                    xhr.onload = () => {
                        resolve({ status: xhr.status, responseText: xhr.responseText });
                    };

                    xhr.onerror = () => {
                        reject(new Error('Network error during chunk upload'));
                    };

                    xhr.open('PUT', uploadUrl, true);
                    xhr.setRequestHeader('Content-Range', `bytes ${start}-${end - 1}/${file.size}`);
                    xhr.send(chunk);
                });

                if (response.status === 308) {
                    // Upload incomplete, move to next chunk
                    start = end;
                    end = Math.min(start + CHUNK_SIZE, file.size);
                    retries = 0; // reset retries on success
                } else if (response.status === 200 || response.status === 201) {
                    // Upload Complete!
                    if (onProgress) onProgress(100);
                    return true;
                } else {
                    // Unexpected status
                    throw new Error(`Upload failed with status ${response.status}: ${response.responseText}`);
                }

            } catch (error) {
                console.warn(`Chunk upload failed, retrying... (${retries + 1}/${MAX_RETRIES})`, error);
                retries++;

                if (retries >= MAX_RETRIES) {
                    throw new Error(`Upload completely failed after ${MAX_RETRIES} chunk retries: ${error.message}`);
                }

                // If a chunk fails, ping the server using a status byte-check to find out where exactly
                // to resume from. This guarantees bulletproof resumption.
                try {
                    const statusRes = await fetch(uploadUrl, {
                        method: 'PUT',
                        headers: {
                            'Content-Range': `bytes */${file.size}`
                        }
                    });

                    if (statusRes.status === 308) {
                        const range = statusRes.headers.get('range');
                        if (range) {
                            // range format "bytes=0-42"
                            const upper = parseInt(range.split('-')[1], 10);
                            start = upper + 1;
                            end = Math.min(start + CHUNK_SIZE, file.size);
                        }
                    } else if (statusRes.status === 200 || statusRes.status === 201) {
                        if (onProgress) onProgress(100);
                        return true; // Somehow it finished successfully
                    }
                } catch (statusError) {
                    // If we can't get status, wait 3 seconds and retry status check on next loop iteration
                    await new Promise(r => setTimeout(r, 3000));
                }
            }
        }

        return true;
    }

    /**
     * Save the media metadata to the Firestore game document so we can display it later.
     */
    async saveMediaMetadata(gameId, uploaderId, uploaderName, fileInfo) {
        try {
            const mediaRef = collection(db, 'games', gameId, 'media');
            await addDoc(mediaRef, {
                fileName: fileInfo.name,
                fileType: fileInfo.type,
                sizeBytes: fileInfo.size,
                uploadedBy: uploaderId,
                uploaderName: uploaderName,
                uploadedAt: serverTimestamp(),
                // We don't have the final Drive file ID here strictly from the PUT request
                // because resumable uploads return it empty if you don't parse the final response perfectly.
                // However, the files are safely in the folder.
                status: 'uploaded_to_drive'
            });
            return true;
        } catch (error) {
            console.error('Error saving media metadata:', error);
            // We don't throw here because the Drive upload actually succeeded
            return false;
        }
    }

    /**
     * Convenience method to handle the entire flow.
     */
    async uploadMediaForGame(file, game, uploader, onProgress) {
        try {
            // 1. Get secure URL
            onProgress(0); // init
            const { uploadUrl } = await this.getUploadUrl(file, game);

            // 2. Upload directly to Google Drive
            await this.uploadFileDirectly(file, uploadUrl, onProgress);

            // 3. Save metadata to Firestore
            await this.saveMediaMetadata(game.id, uploader.id, uploader.name, file);

            return true;
        } catch (error) {
            console.error('Failed complete upload flow:', error);
            throw error;
        }
    }
}

const mediaService = new MediaService();
export default mediaService;
